import {useReports} from '@sail/observability';
import {useCallback, useEffect, useState} from 'react';
import cookie from 'src/internal/cookie/cookie';
import useDataConfig from 'src/public/useDataConfig';

import type {SetCookieOptions} from '@stripe-internal/stripe-cookies';

type CookieSetter = (
  value: string | null,
  options?: SetCookieOptions,
) => Promise<boolean>;
export type CookieHook = [string | null, CookieSetter];

/**
 * @see https://sail.stripe.me/data/api/useCookie
 */
export default function useCookie(
  /** The name of the cookie to read and write */
  name: string,
): CookieHook {
  const dataConfig = useDataConfig();
  const reports = useReports();

  const [cookieValue, setCookieValue] = useState(() =>
    cookie.get(dataConfig.browserStorage, name, reports),
  );

  const setCookie: CookieSetter = useCallback(
    (value, options) =>
      cookie.set(dataConfig.browserStorage, name, value, reports, options),
    [dataConfig.browserStorage, name, reports],
  );

  useEffect(() => {
    function onChange(): void {
      setCookieValue(cookie.get(dataConfig.browserStorage, name, reports));
    }

    cookie.subscribe(dataConfig.browserStorage, onChange, reports);

    return (): void => {
      cookie.unsubscribe(dataConfig.browserStorage, onChange, reports);
    };
  }, [dataConfig.browserStorage, name, reports]);

  return [cookieValue, setCookie];
}
